import type { ReactNode } from 'react';
import Unit from '../Modular/Unit';
import type {
  As as UnitElement,
  UnitSettings,
} from '../Modular/ModularTypes/UnitSettings';
import './Button.css';

export type ButtonElement = Extract<UnitElement, 'button' | 'a'>;

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'carousel'
  | 'signup'
  | 'authPrimary'
  | 'authSecondary';

export type ButtonIntent = '' | 'submit' | 'cancel' | 'danger';

const buttonVariantClasses: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  outline: 'btn-outline',
  carousel: 'btn-carousel',
  signup: 'btn-signup',
  authPrimary: 'btn-auth-primary',
  authSecondary: 'btn-auth-secondary',
};

export type ButtonProps<T extends ButtonElement = 'button'> = {
  as?: T;
  children: ReactNode;
  variant?: ButtonVariant;
  intent?: ButtonIntent | string;
  className?: string;
  ariaLabel?: string;
} & Omit<UnitSettings<T>, 'as' | 'children' | 'className'>;

const Button = <T extends ButtonElement = 'button'>({
  as,
  children,
  variant = 'primary',
  intent = '',
  className = '',
  ariaLabel,
  ...props
}: ButtonProps<T>) => {
  const Component = (as ?? 'button') as T;
  const intentClass = intent ? `intent-${intent}` : '';
  const unitProps = props as UnitSettings<T>;
  const accessibilityProps = ariaLabel ? { 'aria-label': ariaLabel } : {};

  return (
    <Unit<T>
      {...unitProps}
      {...accessibilityProps}
      as={Component}
      className={`btn-base ${buttonVariantClasses[variant]} ${intentClass} ${className}`.trim()}
    >
      {children}
    </Unit>
  );
};

export default Button;
